/**
 * An OAuth/OIDC **error** response handed back by the identity provider.
 *
 * Azure AD B2C reports a failed user flow by redirecting to the app's
 * `redirectUri` with `#error=…&error_description=…` in the URL fragment instead
 * of an authorization code. msal-browser only parses such a fragment while one
 * of its own interactions is flagged in progress — a response that arrives
 * without that flag (or a stale one replayed by `navigateToLoginRequestUrl`) is
 * silently discarded, so nothing in the app ever learns that login failed.
 *
 * These helpers are deliberately framework- and MSAL-independent so both the
 * security lib (which detects the response and stops the retry) and the
 * login-failed page (which displays it) can use them.
 *
 * See BDA-5866 / Zendesk #4429.
 */
export interface BdAuthErrorResponse {
  /** OAuth error code, e.g. `server_error` or `access_denied`. */
  error: string;
  /** Human-readable detail from the provider; empty when it sent none. */
  errorDescription: string;
  /**
   * Provider correlation id, when one can be recovered. B2C embeds it in the
   * error description ("Correlation ID: <uuid>") rather than sending a
   * dedicated parameter — support needs it to look the failure up.
   */
  correlationId: string | null;
}

/**
 * sessionStorage key carrying the failed response from the page load that
 * received it to whatever renders the error.
 *
 * Write-once carrier, never a gate on the auth flow: by the time the router has
 * reached the login-failed route the fragment is gone from the URL, so the
 * details have to survive that hop. Deliberately per-tab.
 */
export const BD_AUTH_ERROR_RESPONSE_KEY = 'bd.auth.error-response';

/**
 * sessionStorage key marking a failed response the user has not been shown yet.
 *
 * Separate from the payload above because the two have different lifetimes: the
 * payload stays available for the support ticket, while this marker is a
 * one-shot gate that the error page clears on arrival. Keeping the gate in
 * storage rather than in memory is deliberate — an in-memory flag dies on a
 * reload, and since the failed response is stripped from the URL there would be
 * nothing left to re-detect it from, so any reload would silently re-arm the
 * failing login (BDA-5866).
 */
export const BD_AUTH_ERROR_PENDING_KEY = 'bd.auth.error-pending';

const CORRELATION_ID_PATTERN = /Correlation ID:\s*([0-9a-f-]{36})/i;

/**
 * Parses an OAuth error response out of a URL fragment or query string.
 * Returns `null` for anything that is not an error response — in particular for
 * a successful `#code=…` response, which must stay MSAL's business.
 *
 * Accepts the fragment with or without its leading `#`/`?`.
 */
export function parseAuthErrorResponse(
  fragmentOrQuery: string | null | undefined,
): BdAuthErrorResponse | null {
  if (!fragmentOrQuery) {
    return null;
  }
  const raw = fragmentOrQuery.replace(/^[#?]/, '');
  if (!raw) {
    return null;
  }
  const params = new URLSearchParams(raw);
  const error = params.get('error');
  if (!error) {
    return null;
  }
  const errorDescription = params.get('error_description') ?? '';
  return {
    error,
    errorDescription,
    correlationId: CORRELATION_ID_PATTERN.exec(errorDescription)?.[1] ?? null,
  };
}

/**
 * Reads an error response from the current location, checking the fragment
 * first (B2C uses `response_mode=fragment`) and then the query string.
 */
export function readAuthErrorResponseFrom(location: {
  hash?: string;
  search?: string;
}): BdAuthErrorResponse | null {
  return (
    parseAuthErrorResponse(location.hash) ??
    parseAuthErrorResponse(location.search)
  );
}

/**
 * Stores the failed response for the error page and marks it as not yet shown.
 * Never throws, and is a no-op without a browser — meta-app renders on the
 * server, where there is no sessionStorage and no redirect response to carry.
 */
export function storeAuthErrorResponse(response: BdAuthErrorResponse): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(
      BD_AUTH_ERROR_RESPONSE_KEY,
      JSON.stringify(response),
    );
    window.sessionStorage.setItem(BD_AUTH_ERROR_PENDING_KEY, 'true');
  } catch {
    // sessionStorage unavailable (private mode, blocked storage) — the error
    // page then simply shows its generic text.
  }
}

/** Reads a previously stored response. Never throws; `null` without a browser. */
export function readStoredAuthErrorResponse(): BdAuthErrorResponse | null {
  if (typeof window === 'undefined') {
    return null;
  }
  let stored: string | null;
  try {
    stored = window.sessionStorage.getItem(BD_AUTH_ERROR_RESPONSE_KEY);
  } catch {
    return null;
  }
  if (!stored) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(stored);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as BdAuthErrorResponse).error === 'string'
    ) {
      return parsed as BdAuthErrorResponse;
    }
    return null;
  } catch {
    return null;
  }
}

/** Drops the stored response and its marker. Never throws. */
export function clearStoredAuthErrorResponse(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.removeItem(BD_AUTH_ERROR_RESPONSE_KEY);
    window.sessionStorage.removeItem(BD_AUTH_ERROR_PENDING_KEY);
  } catch {
    // nothing to clean up when storage is unavailable
  }
}

/**
 * Whether a failed response is still waiting to be shown. While this holds, no
 * *automatic* interactive login may start — otherwise the failing flow runs
 * again and the user never sees why.
 */
export function isAuthErrorPending(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.sessionStorage.getItem(BD_AUTH_ERROR_PENDING_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Consumes the marker — called once the failure has actually been put in front
 * of the user. The payload is deliberately kept so the support ticket can still
 * carry the correlation id after a reload of the error page.
 */
export function clearAuthErrorPending(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.removeItem(BD_AUTH_ERROR_PENDING_KEY);
  } catch {
    // nothing to clean up when storage is unavailable
  }
}
